/**
 * Whether this browser holds a saved Supabase session.
 *
 * supabase-js keeps the session in localStorage under `sb-<host label>-auth-token`
 * (src/integrations/supabase/client.ts). With no such key, the session check
 * can only resolve to "signed out", so signed-out UI can render at once instead
 * of waiting for it. Returns false where there is no browser storage (the
 * prerender pass), so prerendered pages carry the signed-out state.
 *
 * A hint for what to show while loading, never an authorization signal.
 */
const AUTH_TOKEN_KEY = /^sb-.+-auth-token$/;

export function hasStoredAuthSession(
  storage: Pick<Storage, "length" | "key"> | null | undefined = defaultStorage(),
): boolean {
  if (!storage) return false;
  try {
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && AUTH_TOKEN_KEY.test(key)) return true;
    }
    return false;
  } catch {
    // Unreadable storage: assume a session may exist and keep waiting.
    return true;
  }
}

function defaultStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}
